import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Music, Search, ListMusic, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';

const PAGE_SIZE = 20;

/**
 * SongPickerDialog
 *
 * Client-side mode (default):
 *   Pass `availableSongs` + optional `setlistSongs` for two-tab mode.
 *
 * Server-side search mode:
 *   Pass `serverSideSearch={true}` + `onServerSearch` callback.
 *   `onServerSearch(q, lang, cursor) => Promise<{items, has_more, next_cursor}>`
 *   `excludeSongIds` — Set of song_ids already in the lineup (filtered at display time).
 *   In this mode `onAddSong` receives the full song object, not just the song_id.
 */
const SongPickerDialog = ({
  open,
  onOpenChange,
  // Client-side props
  availableSongs,
  totalSongs,
  setlistSongs,
  // Server-side props
  serverSideSearch,
  onServerSearch,
  excludeSongIds,
  // Shared
  onAddSong,
  title,
}) => {
  const [search, setSearch] = useState('');
  const [lang, setLang] = useState('All');
  const [page, setPage] = useState(1);
  const [pickerTab, setPickerTab] = useState('setlist');

  // Server-side state
  const [ssItems, setSsItems] = useState([]);
  const [ssLoading, setSsLoading] = useState(false);
  const [ssHasMore, setSsHasMore] = useState(false);
  const [ssNextCursor, setSsNextCursor] = useState(null);
  const debounceRef = useRef(null);

  const twoTabMode = !serverSideSearch && setlistSongs !== undefined;

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSearch('');
      setLang('All');
      setPage(1);
      setPickerTab(setlistSongs?.length > 0 ? 'setlist' : 'library');
      if (serverSideSearch) {
        setSsItems([]);
        setSsHasMore(false);
        setSsNextCursor(null);
      }
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Trigger server search
  const triggerServerSearch = useCallback(async (q, langFilter, cursor, replace = false) => {
    if (!onServerSearch) return;
    setSsLoading(true);
    try {
      const result = await onServerSearch(q, langFilter, cursor);
      const items = result.items || [];
      setSsItems(prev => replace ? items : [...prev, ...items]);
      setSsHasMore(result.has_more || false);
      setSsNextCursor(result.next_cursor || null);
    } catch {
      /* silent */
    } finally {
      setSsLoading(false);
    }
  }, [onServerSearch]);

  // Initial load + debounced re-fetch on search/lang change
  useEffect(() => {
    if (!serverSideSearch || !open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      triggerServerSearch(search, lang, null, true);
    }, search ? 300 : 0); // no debounce on initial empty-query load
    return () => clearTimeout(debounceRef.current);
  }, [search, lang, serverSideSearch, open, triggerServerSearch]);

  // Scroll handler
  const handleScroll = useCallback((e) => {
    const el = e.target;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (serverSideSearch) {
      if (nearBottom && ssHasMore && !ssLoading) {
        triggerServerSearch(search, lang, ssNextCursor, false);
      }
    } else {
      if (nearBottom && page * PAGE_SIZE < (filteredLibraryRef.current?.length ?? 0)) {
        setPage(p => p + 1);
      }
    }
  }, [serverSideSearch, ssHasMore, ssLoading, ssNextCursor, search, lang, page, triggerServerSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Client-side filters
  const filteredLibrary = useMemo(() => {
    if (serverSideSearch) return [];
    let result = availableSongs || [];
    if (lang !== 'All') result = result.filter(s => s.language === lang);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(s =>
        s.title.toLowerCase().includes(q) || s.artist?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [availableSongs, lang, search, serverSideSearch]);

  const filteredLibraryRef = useRef(filteredLibrary);
  useEffect(() => { filteredLibraryRef.current = filteredLibrary; }, [filteredLibrary]);

  const filteredSetlist = useMemo(() => {
    if (!setlistSongs) return [];
    if (!search.trim()) return setlistSongs;
    const q = search.toLowerCase();
    return setlistSongs.filter(s =>
      s.title?.toLowerCase().includes(q) || s.artist?.toLowerCase().includes(q)
    );
  }, [setlistSongs, search]);

  const visible = useMemo(() => filteredLibrary.slice(0, page * PAGE_SIZE), [filteredLibrary, page]);
  const clientHasMore = visible.length < filteredLibrary.length;

  // Active list for rendering
  const activeList = useMemo(() => {
    if (serverSideSearch) {
      // Filter out songs already in lineup at display time (avoids dep on unstable Set ref)
      return excludeSongIds
        ? ssItems.filter(s => !excludeSongIds.has(s.song_id))
        : ssItems;
    }
    return twoTabMode && pickerTab === 'setlist' ? filteredSetlist : visible;
  }, [serverSideSearch, ssItems, excludeSongIds, twoTabMode, pickerTab, filteredSetlist, visible]);

  const activeHasMore = serverSideSearch ? ssHasMore : (twoTabMode && pickerTab === 'setlist' ? false : clientHasMore);

  const renderSongRow = (song) => (
    <button
      key={song.song_id}
      type="button"
      onClick={() => serverSideSearch ? onAddSong(song) : onAddSong(song.song_id)}
      data-testid={`pick-song-${song.song_id}`}
      className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-green-500/10 border border-transparent hover:border-green-500/25 transition-all flex items-center gap-3"
    >
      <Music className="w-4 h-4 text-green-400 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-900 truncate">{song.title}</p>
        <p className="text-xs text-slate-500 truncate">
          {song.artist}{song.key ? ` · ${song.key}` : ''}{song.language ? ` · ${song.language}` : ''}
        </p>
      </div>
    </button>
  );

  const footerCount = serverSideSearch
    ? `${activeList.length} song${activeList.length !== 1 ? 's' : ''} shown`
    : twoTabMode && pickerTab === 'setlist'
    ? `${filteredSetlist.length} song${filteredSetlist.length !== 1 ? 's' : ''} in setlist`
    : `${filteredLibrary.length} song${filteredLibrary.length !== 1 ? 's' : ''} available`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md sm:max-w-lg p-0 gap-0">
        <DialogHeader className="px-5 pt-5 pb-0">
          <DialogTitle>{title || 'Add Song'}</DialogTitle>
        </DialogHeader>

        {/* Two-tab mode tabs */}
        {twoTabMode && (
          <div className="flex gap-1 px-5 pt-3 pb-0">
            <button
              type="button"
              onClick={() => setPickerTab('setlist')}
              data-testid="picker-tab-setlist"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                pickerTab === 'setlist' ? 'bg-green-700 text-white' : 'text-slate-500 hover:bg-slate-100'
              }`}
            >
              <ListMusic className="w-3.5 h-3.5" />
              From Setlist
              {setlistSongs?.length > 0 && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  pickerTab === 'setlist' ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600'
                }`}>
                  {setlistSongs.length}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setPickerTab('library')}
              data-testid="picker-tab-library"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                pickerTab === 'library' ? 'bg-green-700 text-white' : 'text-slate-500 hover:bg-slate-100'
              }`}
            >
              <Search className="w-3.5 h-3.5" />
              From Library
            </button>
          </div>
        )}

        {/* Search + language filter (library / server-side) */}
        {(!twoTabMode || pickerTab === 'library' || serverSideSearch) && (
          <div className="px-5 pt-3 pb-2 space-y-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="song-picker-search"
                placeholder="Search by title or artist..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  if (!serverSideSearch) setPage(1);
                }}
                className="pl-10 h-9 text-sm"
              />
            </div>
            <div className="flex gap-1" data-testid="song-picker-lang-filter">
              {['All', 'English', 'Filipino'].map(l => (
                <button
                  key={l}
                  type="button"
                  onClick={() => { setLang(l); if (!serverSideSearch) setPage(1); }}
                  data-testid={`picker-lang-${l.toLowerCase()}`}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                    lang === l ? 'bg-green-700 text-white' : 'text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Setlist tab search bar */}
        {twoTabMode && pickerTab === 'setlist' && (
          <div className="px-5 pt-3 pb-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="song-picker-setlist-search"
                placeholder="Search setlist..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-9 text-sm"
              />
            </div>
          </div>
        )}

        {/* Song list */}
        <div
          className="max-h-72 overflow-y-auto px-2 py-2"
          onScroll={handleScroll}
          data-testid="song-picker-list"
        >
          {ssLoading && activeList.length === 0 ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : activeList.length === 0 ? (
            <p className="text-center py-8 text-sm text-slate-500">
              {serverSideSearch
                ? (search ? 'No matching songs found.' : 'No songs available.')
                : twoTabMode && pickerTab === 'setlist'
                ? (setlistSongs?.length === 0
                    ? 'No songs in the setlist yet. Add songs from the Setlist tab first.'
                    : 'No matching songs in setlist.')
                : (totalSongs === 0
                    ? 'No songs in your library.'
                    : search || lang !== 'All'
                    ? 'No matching songs found.'
                    : 'All songs already added.')
              }
            </p>
          ) : (
            <>
              {activeList.map(song => renderSongRow(song))}
              {ssLoading && serverSideSearch && (
                <div className="flex justify-center py-3">
                  <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                </div>
              )}
              {activeHasMore && !serverSideSearch && (
                <div className="text-center py-3">
                  <span className="text-xs text-slate-400">
                    Scroll for more ({filteredLibrary.length - visible.length} remaining)
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-2 border-t border-slate-100 text-xs text-slate-400 text-center">
          {footerCount}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SongPickerDialog;
